import React, { useCallback } from 'react';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import PropTypes from 'prop-types';
import { useChiliPiperContext } from 'Components/ComponentLibrary/Molecules/ChiliPiperComponent/ChiliPiperContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';

export const ManualRolloverContext = React.createContext(null);

const init = (resource) => {
  return {
    manualRolloverResource: resource,
    canSubmit: resource.meta().canPost(),
    attachments: resource.attr('attachments')?.resources().list() || [],
    hasAttachments: resource.attr('attachments')?.resources().list().length > 0,
    loading: false,
  };
};

const SET_MANUAL_ROLLOVER_RESOURCE = 'set-manual-rollover-resource';
const SET_STEP_LOADING = 'set-step-loading';

const reducer = (state, action) => {
  switch (action.type) {
    case SET_MANUAL_ROLLOVER_RESOURCE: {
      return {
        ...state,
        manualRolloverResource: action.manualRolloverResource,
        canSubmit: action.manualRolloverResource.meta().canPost(),
        attachments: action.manualRolloverResource.attr('attachments')?.resources().list() || [],
        hasAttachments: action.manualRolloverResource.attr('attachments')?.resources().list().length > 0,
      };
    }
    case SET_STEP_LOADING: {
      return {
        ...state,
        loading: action.loading,
      };
    }
    default: {
      throw new Error(`Unhandled manualRolloverContext action type: ${action.type}`);
    }
  }
};

export function ManualRolloverContextProvider({ resource, children }) {
  const [state, dispatch] = React.useReducer(reducer, resource, init);
  const { setPageContentCollection, loadPageContentLink } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();

  const setLoading = (loading) => {
    dispatch({ type: SET_STEP_LOADING, loading });
  };

  const setManualRolloverResource = (manualRolloverResource) => {
    dispatch({ type: SET_MANUAL_ROLLOVER_RESOURCE, manualRolloverResource });
  };

  const handleUploadFile = useCallback(async () => {
    loadPageContentLink(resource?.relationship(fr.relationships.uploadFile.name)?.link('related'));
  }, [resource]);

  const refreshResource = useCallback(async () => {
    const refreshedCollection = await resource.link('self').get();
    setManualRolloverResource(refreshedCollection.resources().head());
    setPageContentCollection(refreshedCollection);
  }, [resource]);

  const handleDeleteFile = useCallback(async (fileToDelete) => {
    setLoading(true);
    try {
      await fileToDelete.delete();
      await refreshResource();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  }, [resource]);


  const doRedirect = async () => {
    resource.withRelationship(fr.relationships.redirectOnClose.name,
      async (rel) => {
        try {
          const collection = await rel.link('related').get();
          setPageContentCollection(collection);
        } catch (e) {
          if (e.errors) {
            displayErrorMessage(e, e.errors);
          } else {
            displayErrorMessage(e, 'Failed to redirect');
          }
        }
      },
      () => {
        throw new Error('redirectOnClose relationship not found');
      });
  };

  const {
    setOpenIFrame, showChiliPiperComponent, chiliPiperRel, handleChiliPiperExit,
  } = useChiliPiperContext(resource, doRedirect);

  const onStepSubmit = async () => {
    setLoading(true);
    try {
      if (resource.relationship(fr.relationships.scheduleChiliPiperMeeting.name) && !resource.relationship(fr.relationships.allowConciergeScheduleForNonAcats.name)) {
        setOpenIFrame(true);
        return;
      }

      resource.attr('shouldComplete', 'true');
      const updatedColl = await resource.submit();
      await setPageContentCollection(updatedColl);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const onScheduleChiliPiperForNonAcats = async () => {
    setLoading(true);
    try {
      if (resource.relationship(fr.relationships.scheduleChiliPiperMeeting.name)) {
        setOpenIFrame(true);
        return;
      }

      const updatedColl = await resource.submit();
      await setPageContentCollection(updatedColl);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };


  const memoizedValue = React.useMemo(() => {
    return {
      manualRolloverResource: state.manualRolloverResource,
      canSubmit: state.canSubmit,
      loading: state.loading,
      attachments: state.attachments,
      hasAttachments: state.hasAttachments,
      onStepSubmit,
      onScheduleChiliPiperForNonAcats,
      handleUploadFile,
      handleDeleteFile,
      refreshResource,
      showChiliPiperComponent,
      chiliPiperRel,
      handleChiliPiperExit,
    };
  }, [
    state,
    onStepSubmit,
  ]);

  return (
    <ManualRolloverContext.Provider
      value={memoizedValue}
    >
      {children}
    </ManualRolloverContext.Provider>
  );
}

ManualRolloverContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
  resource: PropTypes.object.isRequired,
};

function useManualRolloverContext() {
  const context = React.useContext(ManualRolloverContext);
  if (!context) throw new Error('useManualRolloverContext must be called from ManualRolloverContext.Provider');
  return context;
}

export default useManualRolloverContext;
